import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createProject } from '../api/projects';
import { createTag } from '../api/tags';
import { createTask } from '../api/tasks';
import { useQueryClient } from '@tanstack/react-query';
import { ModalPortal } from './ModalPortal';
import { useWorkspace } from '../workspace/WorkspaceContext';
import type { RecurrenceDto } from '../api/types';

interface ImportDialogProps {
  onClose: () => void;
}

interface NozbeExport {
  projects: { id: string; name: string; color?: string; isSingleActions?: boolean }[];
  tasks: {
    id: string;
    title: string;
    description?: string | null;
    projectId: string | null;
    dueAt?: string | null;
    recurrenceId?: string | null;
  }[];
  tags: { id: string; name: string; color?: string }[];
  tagAssignments: { taskId: string; tagId: string }[];
  recurrences: { id: string; pattern: string }[];
  starredTaskIds: string[];
}

const COLOR_PALETTE: Record<string, string> = {
  blue: '#2f80ed', green: '#27ae60', red: '#eb5757', orange: '#f2994a',
  purple: '#9b51e0', teal: '#12b5cb', pink: '#ee5f9e', brown: '#8d6e63'
};

function parseRrule(pattern: string): RecurrenceDto | null {
  const freq = /FREQ=(\w+)/.exec(pattern)?.[1];
  if (!freq || !['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].includes(freq)) return null;
  const interval = Number(/INTERVAL=(\d+)/.exec(pattern)?.[1] ?? '1');
  const byday = /BYDAY=([\w,]+)/.exec(pattern)?.[1];
  const dayMap: Record<string, string> = {
    MO: 'MON', TU: 'TUE', WE: 'WED', TH: 'THU', FR: 'FRI', SA: 'SAT', SU: 'SUN'
  };
  const recurrence: RecurrenceDto = {
    unit: freq as RecurrenceDto['unit'],
    interval,
    weekdays: null
  };
  if (byday) {
    recurrence.weekdays = byday
      .split(',')
      .filter((d) => dayMap[d])
      .map((d) => dayMap[d]) as RecurrenceDto['weekdays'];
  }
  return recurrence;
}

/**
 * Imports a Nozbe export (nozbe-export.json) into the CURRENT user's space via
 * the regular REST API — same mapping as migration/import_nozbe.py.
 */
export function ImportDialog({ onClose }: ImportDialogProps) {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<NozbeExport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !running) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, running]);

  const handleFile = async (file: File | undefined) => {
    setError(null);
    setData(null);
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text()) as NozbeExport;
      if (!Array.isArray(parsed.tasks) || !Array.isArray(parsed.projects)) {
        throw new Error('bad shape');
      }
      setData(parsed);
    } catch {
      setError(t('import.badFile'));
    }
  };

  const singleActionIds = new Set(
    (data?.projects ?? []).filter((p) => p.isSingleActions).map((p) => p.id)
  );
  const usedProjectIds = new Set(
    (data?.tasks ?? [])
      .map((task) => task.projectId)
      .filter((id): id is string => id !== null && !singleActionIds.has(id))
  );
  const tagsByTask = new Map<string, string[]>();
  for (const assignment of data?.tagAssignments ?? []) {
    const list = tagsByTask.get(assignment.taskId) ?? [];
    list.push(assignment.tagId);
    tagsByTask.set(assignment.taskId, list);
  }
  const usedTagIds = new Set(
    (data?.tasks ?? []).flatMap((task) => tagsByTask.get(task.id) ?? [])
  );

  const runImport = async () => {
    if (!data || running) return;
    setRunning(true);
    setError(null);
    try {
      const recurrences = new Map(data.recurrences.map((r) => [r.id, r.pattern]));
      const starred = new Set(data.starredTaskIds);

      const projectMap = new Map<string, string>();
      for (const id of usedProjectIds) {
        const source = data.projects.find((p) => p.id === id);
        if (!source) continue;
        setProgress(t('import.creatingProject', { name: source.name }));
        const created = await createProject(workspace.id, {
          name: source.name,
          color: COLOR_PALETTE[source.color ?? ''] ?? '#2f80ed'
        });
        projectMap.set(id, created.id);
      }

      const tagMap = new Map<string, string>();
      for (const id of usedTagIds) {
        const source = data.tags.find((tag) => tag.id === id);
        if (!source) continue;
        setProgress(t('import.creatingTag', { name: source.name }));
        const created = await createTag(workspace.id, {
          name: source.name,
          color: COLOR_PALETTE[source.color ?? '']
        });
        tagMap.set(id, created.id);
      }

      let doneCount = 0;
      for (const task of data.tasks) {
        doneCount += 1;
        setProgress(t('import.creatingTask', { done: doneCount, total: data.tasks.length }));
        const pattern = task.recurrenceId ? recurrences.get(task.recurrenceId) : undefined;
        await createTask(workspace.id, {
          title: task.title,
          description: task.description ?? undefined,
          projectId: task.projectId ? projectMap.get(task.projectId) : undefined,
          dueDate: task.dueAt ? task.dueAt.slice(0, 10) : undefined,
          starred: starred.has(task.id),
          recurrence: pattern ? parseRrule(pattern) ?? undefined : undefined,
          tagIds: (tagsByTask.get(task.id) ?? [])
            .map((tagId) => tagMap.get(tagId))
            .filter((id): id is string => Boolean(id))
        });
      }
      setDone(true);
      setProgress(null);
      qc.invalidateQueries();
    } catch {
      setError(t('import.failed'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <ModalPortal>
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && !running && onClose()}
    >
      <div className="modal">
        <h2>{t('import.title')}</h2>
        <div className="modal-hint">{t('import.hint')}</div>

        {!done && (
          <div className="modal-field">
            <input
              ref={fileRef}
              type="file"
              accept="application/json"
              onChange={(e) => void handleFile(e.target.files?.[0])}
            />
          </div>
        )}

        {data && !done && (
          <div className="modal-hint">
            {t('import.summary', {
              tasks: data.tasks.length,
              projects: usedProjectIds.size,
              tags: usedTagIds.size
            })}
          </div>
        )}

        {progress && <div className="modal-hint">{progress}</div>}
        {error && <div className="modal-hint import-error">{error}</div>}
        {done && <div className="modal-hint">{t('import.done')}</div>}

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={running}>
            {done ? t('common.close') : t('common.cancel')}
          </button>
          {!done && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void runImport()}
              disabled={!data || running}
            >
              {t('import.run')}
            </button>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
